import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
  FormControlLabel,
  TextField,
  Paper,
  Checkbox,
  FormGroup
} from '@material-ui/core';
import { questionnaireApi } from '../api/questionnaireApi';
import { formateurSurveyApi } from '../api/formateurSurveyApi';
import { PUBLIC_FORM, greyAlpha } from '../theme/tones';
import { brandAlpha } from '../theme/tenantBrand';

/* ------------------------------------------------------------------ *
 *  Petits éléments de présentation.
 * ------------------------------------------------------------------ */

/** Icône de contexte choisie d'après le libellé renvoyé par le serveur. */
const ContextIcon = ({ label = '' }) => {
  const key = label.toLowerCase();
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: PUBLIC_FORM.textMuted,
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
  };

  if (key.includes('formateur') || key.includes('intervenant') || key.includes('apprenant')) {
    return (
      <svg {...common}>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c1.5-3.5 4.5-5 8-5s6.5 1.5 8 5" />
      </svg>
    );
  }
  if (key.includes('date') || key.includes('session')) {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M3 10h18" />
      </svg>
    );
  }
  if (key.includes('lieu') || key.includes('salle') || key.includes('format')) {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="13" rx="2" />
        <path d="M9 21h6" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v5" />
    </svg>
  );
};

/** Carte blanche du formulaire : filet fin + ombre diffuse, comme l'app. */
const surfaceCard = {
  backgroundColor: PUBLIC_FORM.surface,
  border: `1px solid ${PUBLIC_FORM.line}`,
  borderRadius: '14px',
  boxShadow: `0 1px 2px ${greyAlpha(900, 0.04)}, 0 8px 24px -16px ${greyAlpha(900, 0.16)}`
};

/**
 * Une réponse est-elle remplie ? Les QCM stockent un tableau JSON, les
 * champs libres une chaîne : la barre de progression doit gérer les deux.
 */
const hasAnswer = (value) => {
  if (!value) return false;
  const raw = String(value).trim();
  if (raw === '') return false;
  if (raw.startsWith('[')) {
    try {
      return JSON.parse(raw).length > 0;
    } catch (e) {
      return true;
    }
  }
  return true;
};

const QuestionnairePublic = () => {
  // Deux routes mènent ici : celle des apprenants (:learnerId) et celle des
  // formateurs (:intervenantId/:sessionId). Le formulaire est le même, seuls
  // le chargement et la soumission changent de destinataire.
  const { id, learnerId, intervenantId, sessionId: routeSessionId } = useParams();
  const isFormateur = Boolean(intervenantId);
  const [loading, setLoading] = useState(true);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  // Post / satisfaction / validation fermés avant le jour de la formation :
  // un refus attendu, pas une panne — l'écran se présente autrement.
  const [notOpenYet, setNotOpenYet] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        const tenant = queryParams.get('tenant');
        const sessionId = queryParams.get('session_id');
        const res = isFormateur
          ? await formateurSurveyApi.fetchPublic(id, intervenantId, routeSessionId, tenant)
          : await questionnaireApi.fetchPublic(id, learnerId, tenant, sessionId);
        if (res.status === true) {
          setQuestionnaire(res.data);
          const initialAnswers = {};
          res.data.questions.forEach(q => {
            initialAnswers[q.id] = '';
          });
          setAnswers(initialAnswers);
        } else {
          setNotOpenYet(res.reason === 'not_open_yet');
          setError(res.message || 'Questionnaire introuvable.');
        }
      } catch (err) {
        setError('Une erreur est survenue lors du chargement.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, learnerId, intervenantId, routeSessionId]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const tenant = queryParams.get('tenant');
      const contextSessionId = queryParams.get('session_id');
      const contextFormationId = queryParams.get('formation_id');

      const res = isFormateur
        ? await formateurSurveyApi.submit({
          questionnaire_id: id,
          intervenant_id: intervenantId,
          session_id: routeSessionId,
          answers
        }, tenant)
        : await questionnaireApi.submitQuestionnaire({
          questionnaire_id: id,
          customer_id: learnerId,
          answers,
          session_id: contextSessionId,
          formation_id: contextFormationId
        }, tenant);
      if (res.status) {
        setSubmitted(true);
      } else {
        setNotOpenYet(res.reason === 'not_open_yet');
        setError(res.message || 'Erreur lors de l\'envoi.');
      }
    } catch (err) {
      setError('Impossible d\'envoyer vos réponses.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Fond de page commun à tous les états (chargement, erreur, formulaire). */
  const pageBackground = {
    minHeight: '100vh',
    backgroundColor: PUBLIC_FORM.canvas,
    backgroundImage: `radial-gradient(700px 320px at 15% -5%, ${PUBLIC_FORM.accentSoft}, transparent 60%)`,
    fontFamily: PUBLIC_FORM.font
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={pageBackground}>
        <CircularProgress sx={{ color: PUBLIC_FORM.accent }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="flex-start" sx={{ ...pageBackground, pt: 8 }}>
        <Container maxWidth="sm">
          <Paper elevation={0} sx={{ ...surfaceCard, overflow: 'hidden' }}>
            <Box sx={{ height: 3, backgroundColor: notOpenYet ? PUBLIC_FORM.accent : PUBLIC_FORM.danger }} />
            <Box sx={{ p: 4 }}>
              <Typography
                sx={{
                  fontFamily: PUBLIC_FORM.fontDisplay,
                  fontSize: '1.5rem',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: PUBLIC_FORM.text
                }}
              >
                {notOpenYet ? 'Pas encore disponible' : 'Une difficulté est survenue'}
              </Typography>
              <Typography sx={{ color: PUBLIC_FORM.textMuted, fontSize: '0.9rem', mt: 1.5, lineHeight: 1.6 }}>
                {error}
              </Typography>
              <Box mt={3.5} sx={{ display: notOpenYet && !questionnaire ? 'none' : 'block' }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (!questionnaire) {
                      window.location.reload();
                    } else {
                      setError(null);
                    }
                  }}
                  sx={{
                    background: PUBLIC_FORM.cta,
                    color: PUBLIC_FORM.surface,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '10px',
                    px: 3,
                    py: 1.2,
                    boxShadow: 'none'
                  }}
                >
                  {questionnaire ? 'Fermer' : 'Réessayer'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box display="flex" justifyContent="center" alignItems="flex-start" sx={{ ...pageBackground, pt: 8 }}>
        <Container maxWidth="sm">
          <Paper elevation={0} sx={{ ...surfaceCard, overflow: 'hidden' }}>
            <Box sx={{ height: 3, background: PUBLIC_FORM.aurora }} />
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  mx: 'auto',
                  mb: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: PUBLIC_FORM.accentSoft
                }}
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={PUBLIC_FORM.accent} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12.5 L9.5 18 L20 6.5" />
                </svg>
              </Box>
              <Typography
                sx={{
                  fontFamily: PUBLIC_FORM.fontDisplay,
                  fontSize: '1.5rem',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: PUBLIC_FORM.primary
                }}
              >
                Merci pour votre retour
              </Typography>
              <Typography sx={{ color: PUBLIC_FORM.textMuted, fontSize: '0.9rem', mt: 1.5, lineHeight: 1.6 }}>
                Vos réponses ont bien été enregistrées.
              </Typography>
              <Typography sx={{ color: PUBLIC_FORM.textFaint, fontSize: '0.8rem', mt: 3 }}>
                {questionnaire.name}
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  const questions = questionnaire.questions || [];
  const answeredCount = questions.filter((q) => hasAnswer(answers[q.id])).length;
  const progress = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

  return (
    <Box sx={{ ...pageBackground, py: 5 }}>
      <Container maxWidth="sm">
        <form onSubmit={handleSubmit}>

          {/* ------------------------------------------------ En-tête -- */}
          <Paper elevation={0} sx={{ ...surfaceCard, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ height: 3, background: PUBLIC_FORM.aurora }} />
            <Box sx={{ p: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: '12px',
                    background: PUBLIC_FORM.brandTile,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 6px 14px -6px ${brandAlpha(PUBLIC_FORM.primary, 0.5)}`
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: PUBLIC_FORM.fontDisplay,
                      fontWeight: 800,
                      fontSize: '1.25rem',
                      color: PUBLIC_FORM.surface
                    }}
                  >
                    S
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: PUBLIC_FORM.link
                    }}
                  >
                    {questionnaire.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: PUBLIC_FORM.textMuted, mt: 0.25 }}>
                    Vos réponses aident à améliorer nos formations
                  </Typography>
                </Box>
              </Box>

              {questionnaire.formation_name && (
                <Typography
                  sx={{
                    fontFamily: PUBLIC_FORM.fontDisplay,
                    fontSize: '1.4rem',
                    lineHeight: 1.25,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: PUBLIC_FORM.primary,
                    mt: 2.25
                  }}
                >
                  {questionnaire.formation_name}
                </Typography>
              )}

              {/* Formateur, date, lieu… : pré-remplis par le serveur depuis la
                  fiche intervenant et la session, affichés en lecture seule. */}
              {questionnaire.context && Object.keys(questionnaire.context).length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {Object.entries(questionnaire.context).map(([label, value]) => (
                    <Box
                      key={label}
                      title={label}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '999px',
                        backgroundColor: PUBLIC_FORM.surface,
                        border: `1px solid ${PUBLIC_FORM.lineStrong}`,
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: PUBLIC_FORM.textMuted
                      }}
                    >
                      <ContextIcon label={label} />
                      {value}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>

          {/* --------------------------------------------- Progression -- */}
          {questions.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, px: 0.5, mb: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: '999px',
                  backgroundColor: PUBLIC_FORM.lineStrong,
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    width: `${progress}%`,
                    height: '100%',
                    borderRadius: '999px',
                    background: PUBLIC_FORM.cta,
                    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: PUBLIC_FORM.textMuted,
                  whiteSpace: 'nowrap'
                }}
              >
                {answeredCount} sur {questions.length}
              </Typography>
            </Box>
          )}

          {/* ---------------------------------------------- Questions -- */}
          {questions.map((q, index) => {
            const options = typeof q.options === 'string' ? JSON.parse(q.options) : q.options;
            // Côté formateur, les champs libres (« Remarques », commentaires)
            // sont facultatifs dans le formulaire de référence.
            const isRequired = !isFormateur || q.type !== 'text';

            return (
              <Paper
                key={q.id}
                elevation={0}
                sx={{
                  ...surfaceCard,
                  p: 3,
                  mb: 2,
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:focus-within': {
                    borderColor: PUBLIC_FORM.accent,
                    boxShadow: `0 0 0 4px ${PUBLIC_FORM.accentSoft}`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      borderRadius: '8px',
                      backgroundColor: PUBLIC_FORM.accentSoft,
                      color: PUBLIC_FORM.accentDark,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      lineHeight: 1.45,
                      color: PUBLIC_FORM.text,
                      pt: 0.25
                    }}
                  >
                    {q.text}
                    {isRequired ? (
                      <Box component="span" sx={{ color: PUBLIC_FORM.danger, ml: 0.5 }}>*</Box>
                    ) : (
                      <Box
                        component="span"
                        sx={{ ml: 0.75, fontSize: '0.78rem', fontWeight: 500, color: PUBLIC_FORM.textFaint }}
                      >
                        (facultatif)
                      </Box>
                    )}
                  </Typography>
                </Box>

                {q.type === 'qcm' || q.type === 'mcq' ? (
                  <FormGroup sx={{ gap: 1 }}>
                    {options && options.map((opt, idx) => {
                      const optText = typeof opt === 'object' ? (opt.text || '') : opt;
                      const optValue = typeof opt === 'object' ? (opt.text || '') : opt;
                      const getCurrentAnswers = (ans) => {
                        if (!ans) return [];
                        try {
                          const parsed = JSON.parse(ans);
                          return Array.isArray(parsed) ? parsed : [ans];
                        } catch (e) {
                          return ans.split(',');
                        }
                      };
                      const currentAnswers = getCurrentAnswers(answers[q.id]);
                      const isChecked = currentAnswers.includes(optValue);

                      return (
                        <FormControlLabel
                          key={idx}
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                let newAnswers;
                                if (e.target.checked) {
                                  newAnswers = [...currentAnswers, optValue];
                                } else {
                                  newAnswers = currentAnswers.filter(a => a !== optValue);
                                }
                                handleAnswerChange(q.id, JSON.stringify(newAnswers));
                              }}
                              sx={{
                                p: 0,
                                mr: 1.5,
                                color: PUBLIC_FORM.lineStrong,
                                '&.Mui-checked': { color: PUBLIC_FORM.accent }
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontSize: '0.9rem',
                                fontWeight: isChecked ? 600 : 400,
                                color: isChecked ? PUBLIC_FORM.accentDark : PUBLIC_FORM.text
                              }}
                            >
                              {optText}
                            </Typography>
                          }
                          // Toute la rangée est cliquable : cible de 46 px,
                          // confortable au doigt sur mobile.
                          sx={{
                            m: 0,
                            minHeight: 46,
                            px: 1.75,
                            py: 1,
                            borderRadius: '10px',
                            border: '1px solid',
                            borderColor: isChecked ? PUBLIC_FORM.accent : PUBLIC_FORM.lineStrong,
                            backgroundColor: isChecked ? PUBLIC_FORM.accentSoft : PUBLIC_FORM.surface,
                            transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              borderColor: PUBLIC_FORM.accent,
                              backgroundColor: PUBLIC_FORM.accentSoft
                            }
                          }}
                        />
                      );
                    })}
                  </FormGroup>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Votre réponse…"
                    value={answers[q.id] || ''}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        fontSize: '0.9rem',
                        color: PUBLIC_FORM.text,
                        backgroundColor: PUBLIC_FORM.surface
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: PUBLIC_FORM.lineStrong
                      },
                      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: PUBLIC_FORM.accent
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: PUBLIC_FORM.accent,
                        borderWidth: 1
                      }
                    }}
                  />
                )}
              </Paper>
            );
          })}

          {/* ------------------------------------------------- Envoi -- */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mt: 3
            }}
          >
            <Button
              type="submit"
              disabled={isSubmitting}
              endIcon={!isSubmitting && (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              )}
              sx={{
                background: PUBLIC_FORM.cta,
                color: PUBLIC_FORM.surface,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.92rem',
                px: 3.5,
                py: 1.5,
                borderRadius: '10px',
                boxShadow: `0 8px 18px -8px ${PUBLIC_FORM.accent}`,
                '&:hover': {
                  background: PUBLIC_FORM.cta,
                  filter: 'brightness(0.94)',
                  boxShadow: `0 12px 22px -8px ${PUBLIC_FORM.accent}`
                },
                '&:disabled': {
                  background: PUBLIC_FORM.disabledBg,
                  color: PUBLIC_FORM.disabledText,
                  boxShadow: 'none'
                }
              }}
            >
              {isSubmitting
                ? <CircularProgress size={20} sx={{ color: PUBLIC_FORM.disabledText }} />
                : 'Envoyer mes réponses'}
            </Button>

            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={PUBLIC_FORM.textFaint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="10" width="16" height="10" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
              <Typography sx={{ fontSize: '0.75rem', color: PUBLIC_FORM.textFaint }}>
                Vos réponses restent confidentielles
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ textAlign: 'center', fontSize: '0.75rem', color: PUBLIC_FORM.textFaint, mt: 4 }}>
            Propulsé par Syndays
          </Typography>

        </form>
      </Container>
    </Box>
  );
};

export default QuestionnairePublic;
